'use client';

import { Button, Container, Text } from '@lmn/design-system';
import React, { type HTMLAttributes, useState } from 'react';
import styled, { css } from 'styled-components';
// import { animated, useSpring } from 'react-spring';
import { IconArrowUp } from '@/app/common/icons/libraryIcons';
// import { useMeasure } from 'react-use';
import type { Brand } from '@/domainLogic/index.ts';
import customCss from '@/app/components/hotel-detail/css/customCss';
import {
  type ExtendedSortedService,
  getServicePreviewLabel,
  useExtendedSortedHotelServices,
} from '@/app/components/hotel-detail/hooks/useExtendedSortedHotelServices';
import { HotelPageContentSection } from '@/app/components/hotel-detail/HotelPageContentSection';
import { useTranslation } from '@/app/components/translations/Translation';
import { CallBackLink } from '@/app/components/hotel-detail/CallBackLink';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { TextBase } from '@/app/components/hotel-detail/common/text';
import { Icon } from '@/app/common/icons/Icon';
import { FACILITY_ICONS } from '@/app/components/hotel-detail/hooks/useExtendedSortedHotelServices';
import { useIsMobile } from './hooks/useMediaSSR';
import OpenXModal from '../search/components/Modal/OpenXModal';
import BasicModalHeader from '../search/components/Modal/LayoutElements/BasicModalHeader';
import { useFetchHotelStatic } from './hooks/useFetchHotelStatic';

type HotelPageFacilitiesSectionProps = HTMLAttributes<HTMLElement>;

// const StyledDiv = styled.div``;
// const AnimatedDiv = animated(StyledDiv);

const MostPopularTitle = styled(Text).attrs({ variant: 'Title5' })`
  margin-bottom: 8px;
`;

const CategoryContainer = styled.div<{ $breakColumn: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-bottom: 24px;
  ${({ theme }) => theme.kallisto.mixins.font('BodySmall')};

  ${({ $breakColumn }) => ($breakColumn ? 'break-inside: auto' : 'break-inside: avoid')};
`;

const CategoryTitle = styled(Text).attrs({ variant: 'Title5' })`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
  svg {
    width: 20px;
    height: 20px;
  }
`;

const ServiceList = styled.ul`
  padding: 0 0 0 2px;

  li {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  li:not(:last-child) {
    margin-bottom: 8px;
  }
`;

const Service = styled.li`
  display: flex;
  align-items: flex-start;
  break-inside: avoid-column;

  svg {
    width: 16px;
    height: 20px;
    margin: 0 10px 0 0;
    flex-shrink: 0;
  }
`;

const Separator = styled.hr`
  margin: 24px 0;
  border: 0;
  border-top: 1px solid ${(p) => p.theme.commons.colors.gray100};
`;

export default function HotelPageFacilitiesSection(props: HotelPageFacilitiesSectionProps) {
  const { t } = useTranslation();
  const [isOpen, setOpen] = useState(false);
  const { commonData, localization } = usePageWrapperData();
  const isMobile = useIsMobile(commonData.isMobileDevice);
  const { data: staticData } = useFetchHotelStatic();
  const selectedServices = useExtendedSortedHotelServices(staticData?.services);

  const showModal = selectedServices && (selectedServices.length > 1 || selectedServices[0]?.services.length > 1);

  return selectedServices && selectedServices.length > 0 ? (
    <HotelPageContentSection
      {...props}
      title={t('vacation.select.facilities.title')}
      css={css`
        background-color: white;

        @media ${customCss.breakpoints.sm} {
          background-color: inherit;
        }
      `}
    >
      {isMobile ? (
        <div>
          <MostPopularFacilities services={selectedServices} />
          {showModal && (
            <>
              <Button
                variant="primary"
                appearance="outlined"
                onClick={() => setOpen(true)}
                css={`
                  width: 100%;
                  margin-top: 24px;
                `}
              >
                {t('vacation.select.facilities.showmore')}
              </Button>
              <OpenXModal
                isOpen={isOpen}
                isMobile={true}
                brand={localization.brand as Brand}
                onClose={() => setOpen(false)}
                onCloseByBrowserHistory={() => setOpen(false)}
                onOpenByBrowserHistory={() => setOpen(true)}
                fullScreen={false}
                id="hotel-facilities-modal"
                aria-label={t('vacation.select.facilities.title')}
                bodyBackgroundColor="backgroundPage"
                header={{
                  sticky: true,
                  element: (
                    <BasicModalHeader handleClose={() => setOpen(false)}>
                      {t('vacation.select.facilities.title')}
                    </BasicModalHeader>
                  ),
                }}
              >
                <HotelFacilitiesDialogContent services={selectedServices} />
              </OpenXModal>
            </>
          )}
        </div>
      ) : (
        <div
          css={`
            @media ${customCss.breakpoints.sm} {
              border-radius: 8px;
              background-color: white;
              padding: 24px;
            }
          `}
        >
          <MostPopularTitle>{t('vacation.select.facilities.popular')}</MostPopularTitle>
          <MostPopularFacilities services={selectedServices} />
          <Separator />
          <ServicesList services={selectedServices} />
          {/* TODO REV - Sergi needs to check this with legal. Add the key to validKeys */}
          {/* <Text variant="BodySmall" css={css`color: ${p => p.theme.commons.colors.gray400};`}>{translateWithElements('vacation.select.facilities.disclaimer', { hotelName: <span>{staticData?.name ?? ''}</span>, newLine: <br/> })}</Text> */}
        </div>
      )}
    </HotelPageContentSection>
  ) : undefined;
}

export function HotelFacilitiesDialogContent(props: { services: ExtendedSortedService[] }) {
  const { services } = props;
  const { t } = useTranslation();

  return (
    <Container fill="gray0">
      <MostPopularTitle>{t('vacation.select.facilities.popular')}</MostPopularTitle>
      <MostPopularFacilities services={services} />
      <Separator />
      {services.map((servicesCategory) => (
        <ServicesCategory key={servicesCategory.category} category={servicesCategory} />
      ))}
    </Container>
  );
}

export function MostPopularFacilities(props: { services: ExtendedSortedService[] }) {
  const { services } = props;
  const { t } = useTranslation();

  return (
    <ul
      css={css`
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        gap: 2px 16px;
        padding: 0;
        margin: 0;

        @media ${customCss.breakpoints.sm} {
          column-gap: 24px;
        }
      `}
    >
      {services.map((service) => (
        <li
          css={`
            display: flex;
            gap: 8px;
            align-items: center;
            padding: 4px 0;
          `}
          key={service.category}
        >
          <Icon {...FACILITY_ICONS[service.icon]} size={20} alt={service.icon} />
          <TextBase>{getServicePreviewLabel(service, t)}</TextBase>
        </li>
      ))}
    </ul>
  );
}

function ServicesList(props: { services: ExtendedSortedService[] }) {
  const { services } = props;
  const [isOpen, setOpen] = useState(false);
  const { t } = useTranslation();
  // const [ref, { height }] = useMeasure();

  // TODO Rev - Latest mockups don't have a read more button
  // const totalServices = services.reduce((acc, curr) => acc + curr.services.length, 0);
  const shouldShowReadMore = false; // totalServices > 20;

  // const style = useSpring({
  //   from: { maxHeight: shouldShowReadMore ? 140 : height },
  //   to: { maxHeight: isOpen || !shouldShowReadMore ? height : 140 },
  // });

  return (
    <>
      {/* <AnimatedDiv id="showmore-services-content" style={{ overflow: 'hidden', ...style }}>         */}
      <div
        // ref={ref as React.Ref<HTMLDivElement>}
        css={`
          column-gap: 50px;
          @media ${customCss.breakpoints.sm} {
            column-count: 2;
          }

          @media ${customCss.breakpoints.md} {
            column-count: 3;
          }
        `}
      >
        {services.map((servicesCategory) => (
          <ServicesCategory key={servicesCategory.category} category={servicesCategory} />
        ))}
      </div>
      {/* </AnimatedDiv> */}

      {shouldShowReadMore ? (
        <div
          id="showmore-amenities-button"
          css={`
            display: inline-block;
            cursor: pointer;
            margin-left: -0.5rem;
            margin-top: 1.5rem;
            padding: 0 0.5rem;
          `}
        >
          <CallBackLink
            role="button"
            variant="accent"
            onClick={() => setOpen((prev) => !prev)}
            css={`
              display: flex;
              align-items: center;
            `}
          >
            {isOpen ? t('vacation.select.facilities.showless') : t('vacation.select.facilities.showmore')}
            <IconArrowUp
              css={css`
                height: 1.25rem;
                width: 1.25rem;
                transform: rotate(${isOpen ? '0deg' : '180deg'});
                transition: transform 0.2s;
                margin-left: 8px;
                color: ${(p) => p.theme.commons.colors.primary400};

                @media ${customCss.breakpoints.sm} {
                  height: 1rem;
                  width: 1rem;
                }
              `}
            />
          </CallBackLink>
        </div>
      ) : null}
    </>
  );
}

function ServicesCategory(props: { category: ExtendedSortedService }) {
  const { category } = props;
  const { t } = useTranslation();

  return (
    <CategoryContainer $breakColumn={category.category === 'GENERAL'}>
      <CategoryTitle>
        <Icon {...FACILITY_ICONS[category.icon]} size={20} alt={category.icon} />
        {t(category.translationKey)}
      </CategoryTitle>
      <ServiceList>
        {category.services.map((service) => (
          <Service key={service}>
            <Icon category="Semantics" name="icon-check" size={16} color="iconPrimary" alt={category.icon} />
            <TextBase>{service}</TextBase>
          </Service>
        ))}
      </ServiceList>
    </CategoryContainer>
  );
}
